<template>
	<section class="vouchers-buy insicht insicht--fade-up">
		<div
      class="container container--narrow-plus"
      v-if="initialError"
    >
      <GeneralMessage
				ref="initialError"
				v-if="initialError"
				:message="initialError"
				type="error"
			/>
		</div>
		<div class="container">
			<ul class="vouchers__list">
				<li
					class="vouchers__item"
					v-for="(voucher, index) in vouchers"
					:key="index"
				>
					<img
						class="vouchers__item-image"
						:src="voucher.image"
						:alt="voucher.title"
					>
					<span
						class="vouchers__item-title"
						v-html="voucher.title"
					/>
					<p
						class="vouchers__item-text"
						v-html="voucher.text"
					/>
					<div class="vouchers__item-price">
						<span
							class="vouchers__item-price-value"
							v-if="!voucher.custom"
							v-html="`${$options.filters.fwgpricecent(voucher.price * 100)} / ${$t('vouchers.piece')}`"
						/>
						<template v-else>
							<span
								class="vouchers__item-price-prefix"
								v-html="'CHF'"
							/>
							<input
								v-model.number="voucher.price"
								type="number"
								min="0"
								step="1"
								class="vouchers__item-price-input"
								@input="onInput(voucher)"
							>
							<span
								class="vouchers__item-price-suffix"
								v-html="`/ ${$t('vouchers.piece')}`"
							/>
						</template>
					</div>
					<QuantityCounter
						:quantity="voucher.quantity"
						base-class="vouchers__item-quantity"
						@sub="onSub(voucher)"
						@add="onAdd(voucher)"
            :locked="!voucher.price"
					/>
				</li>
			</ul>
		</div>

    <div
			class="vouchers__sum"
			v-if="showSum"
		>
			<div
				class="vouchers__sum-item"
				v-for="(voucher, index) in activeVouchers"
				:key="index"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="voucher.title"
				/>
				<span
					class="vouchers__sum-item-quantity"
					v-html="voucher.quantity"
				/>
				<span
					class="vouchers__sum-item-price"
					v-html="$options.filters.fwgpricecent(voucher.quantity * (voucher.price * 100))"
				/>
			</div>
			<div class="vouchers__sum-total">
				<span
					class="vouchers__sum-total-title"
					v-html="$t('vouchers.total')"
				/>
				<span
					class="vouchers__sum-total-price"
					v-html="$options.filters.fwgpricecent(total)"
				/>
			</div>
		</div>

		<div
			class="vouchers__sum vouchers__sum--empty"
			v-else
		>
			<div
				class="vouchers__sum-item"
			>
				<span
					class="vouchers__sum-item-title"
					v-html="$t('vouchers.noVouchersSelected')"
				/>
			</div>
		</div>

		<div
			class="vouchers__sticky"
			v-if="showSum"
		>
			<div class="vouchers__sticky-content">
				<div class="vouchers__sticky-count">
					<div
						class="vouchers__sticky-count-label"
						v-html="$t('vouchers.vouchers')"
					/>
					<div
						class="vouchers__sticky-count-value"
						v-html="totalCount"
					/>
				</div>

				<div class="vouchers__sticky-total">
					<div
						class="vouchers__sticky-total-label"
						v-html="$t('vouchers.total')"
					/>
					<div
						class="vouchers__sticky-total-value"
						v-html="$options.filters.fwgpricecent(total)"
					/>
				</div>
			</div>
			<Button
				type="button"
				level="primary"
				:text="$t('vouchers.orderButton')"
				@click="scrollToForm"
			/>
		</div>

		<div
			class="vouchers__form"
			v-if="showSum"
		>
			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				class="vouchers__form-wrapper"
				novalidate
				@submit.prevent="onSubmit"
			>
        <GeneralMessage
          ref="message"
          v-if="response.message"
          :message="response.message"
          :type="response.type"
          stick
        />
				<FormInputCheckbox
					name="print"
					value="printAtHome"
					v-model="form.printAtHome"
          :hint="$t('vouchers.printAtHomeHint')"
					immediate
				>
					<span v-html="$t('vouchers.printAtHomeLabel')"/>
				</FormInputCheckbox>

				<h2
					class="vouchers__form-title"
					v-html="$t('vouchers.contactTitle')"
				/>

				<div class="form-row">
					<ValidationProvider
						class="form-row__wrapper"
						name="email"
						rules="required|email"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
					>
						<FormInput
							type="email"
							:placeholder="$t('forms.labels.email')"
							:label="$t('forms.labels.email')"
							name="email"
							v-model="form.email"
							:error="errors.length > 0"
              :error-message="errors[0]"
              :hint="$t('vouchers.emailHint')"
							required
						/>
					</ValidationProvider>

					<ValidationProvider
						class="form-row__wrapper"
						name="shippingPhone"
						rules="phone"
						v-slot="{ errors, invalid, validated }"
						tag="div"
						:customMessages="{ phone: $t('forms.errors.phone') }"
					>
						<FormInput
							type="tel"
							:placeholder="$t('forms.labels.phone')"
							:label="$t('forms.labels.phone')"
							:labelOption="$t('forms.labels.optional')"
							name="phone"
							v-model="form.phoneNumber"
							:error="errors.length > 0"
              :error-message="errors[0]"
              :hint="$t('vouchers.phoneHint')"
						/>
					</ValidationProvider>
				</div>

				<template v-if="!form.printAtHome">
					<FormInputCheckbox
						name="gift"
						value="sendAsGift"
						v-model="form.sendAsGift"
						immediate
					>
						<span v-html="$t('vouchers.asGiftLabel')"/>
					</FormInputCheckbox>
				</template>

				<template v-if="form.sendAsGift || form.printAtHome">
					<h2
						class="vouchers__form-title"
						v-html="$t('vouchers.billingAddressTitle')"
					/>
					<ValidationObserver
						tag="section"
						ref="billingAddress"
						vid="billingAddress"
						v-slot="{ invalid }"
					>

						<div class="form-row">
							<ValidationProvider
								class="form-row__wrapper"
								name="billingFirstname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="form.billingAddress.firstname"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>
							<ValidationProvider
								class="form-row__wrapper"
								name="billingLastname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="form.billingAddress.lastname"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>
						</div>

						<div class="form-row">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.company')"
								:label="$t('forms.labels.company')"
								:labelOption="$t('forms.labels.optional')"
								name="company"
								v-model="form.billingAddress.company"
								:required="false"
							/>
						</div>

						<div class="form-row">
							<ValidationProvider
								class="form-row__wrapper"
								name="billingStreet"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.streetNumber')"
									:label="$t('forms.labels.streetNumber')"
									name="street"
									v-model="form.billingAddress.street"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>

							<ValidationObserver
								class="form-row__wrapper"
								tag="div"
								ref="billingZipCity"
								v-slot="{ dirty, validated, invalid, errors }"
							>
								<div class="form-group">
									<ValidationProvider
										class="form-group__span-4 form-group__span-xxl-3"
										name="billingZip"
										rules="required|digits:4"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
									>
										<FormInput
											ref="zipInputBilling"
											type="text"
											:placeholder="$t('forms.labels.zip')"
											:label="$t('forms.labels.zip')"
											@keydown="onZipKeydown"
											@input="onZipInputBilling"
											name="zip"
											v-model="form.billingAddress.postcode"
											:minlength="4"
											:maxlength="4"
											inputmode="decimal"
											pattern="[0-9]{4}"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="form-group__span-8 form-group__span-xxl-9"
											name="billingCity"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.city')"
											:label="$t('forms.labels.city')"
											name="city"
											v-model="form.billingAddress.city"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error form-group__span-12"
									v-if="invalid && (validated || dirty)"
									v-html="joinErrorMessage(errors)"
								/>
							</ValidationObserver>
						</div>

						<div class="form-row">
							<FormInputSelect
								:options="countryOptions"
								v-model="form.billingAddress.countryCode"
								:label="$t('forms.labels.country')"
								name="country"
							/>
						</div>

					</ValidationObserver>

				</template>

				<template v-if="!form.printAtHome">
					<h2
						class="vouchers__form-title"
						v-html="$t('vouchers.addressTitle')"
					/>

					<ValidationObserver
						tag="section"
						ref="shippingAddress"
						vid="shippingAddress"
						v-slot="{ invalid }"
					>

						<div class="form-row">
							<ValidationProvider
								class="form-row__wrapper"
								name="shippingFirstname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="form.shippingAddress.firstname"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>
							<ValidationProvider
								class="form-row__wrapper"
								name="shippingLastname"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="form.shippingAddress.lastname"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>
						</div>

						<div class="form-row">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.company')"
								:label="$t('forms.labels.company')"
								:labelOption="$t('forms.labels.optional')"
								name="company"
								v-model="form.shippingAddress.company"
								:required="false"
							/>
						</div>

						<div class="form-row">
							<ValidationProvider
								class="form-row__wrapper"
								name="shippingStreet"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.streetNumber')"
									:label="$t('forms.labels.streetNumber')"
									name="street"
									v-model="form.shippingAddress.street"
									:error="errors.length > 0"
                  :error-message="errors[0]"
									required
								/>
							</ValidationProvider>

							<ValidationObserver
								class="form-row__wrapper"
								tag="div"
								ref="shippingZipCity"
								v-slot="{ dirty, validated, invalid, errors }"
							>
								<div class="form-group">
									<ValidationProvider
										class="form-group__span--4 form-group__span--xxl-3"
										name="shippingZip"
										rules="required|digits:4"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
									>
										<FormInput
											ref="zipInput"
											type="text"
											:placeholder="$t('forms.labels.zip')"
											:label="$t('forms.labels.zip')"
											@keydown="onZipKeydown"
											@input="onZipInput"
											name="zip"
											v-model="form.shippingAddress.postcode"
											:minlength="4"
											:maxlength="4"
											inputmode="decimal"
											pattern="[0-9]{4}"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
											class="form-group__span--8 form-group__span--xxl-9"
											name="shippingCity"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.city')"
											:label="$t('forms.labels.city')"
											name="city"
											v-model="form.shippingAddress.city"
											:error="errors.length > 0"
											required
										/>
									</ValidationProvider>
								</div>
								<div
									class="input-error form-group__span--12"
									v-if="invalid && (validated || dirty)"
									v-html="joinErrorMessage(errors)"
								/>
							</ValidationObserver>
						</div>

						<div class="form-row">
							<FormInputSelect
								:options="countryOptions"
								v-model="form.shippingAddress.countryCode"
								:label="$t('forms.labels.country')"
								name="country"
							/>
						</div>

					</ValidationObserver>
				</template>

				<div class="form-row">
					<FormInputTextarea
            :placeholder="$t('forms.labels.notes')"
            :label="$t('forms.labels.notes')"
            v-model="form.message"
          />
				</div>


				<ValidationProvider
					:rules="{ required: { allowFalse: false } }"
					name="checkAgb"
					tag="div"
					v-slot="{ invalid, errors, validated }"
					:customMessages="{ required: $t('vouchers.errorTerms') }"
				>
					<FormInputCheckbox
						name="terms"
						value="checkAgb"
						v-model="form.checkAgb"
						:error="errors.length > 0"
						:error-message="errors[0]"
						immediate
					>
						<span v-html="$t('vouchers.termsLabel')"/>
					</FormInputCheckbox>
				</ValidationProvider>

				<Button
					type="submit"
					level="primary"
					:text="$t('vouchers.orderButton')"
					:disabled="formLocked"
				/>
			</ValidationObserver>
		</div>

		<Modal
			ref="videoModal"
		>
			<div
				class="overlay-video"
				v-html="videoEmbedCode"
			/>
		</Modal>
	</section>
</template>

<script>
import Vue from 'vue'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelect"
import GenericMixin from '@/mixins/GenericVueMixin'
import QuantityCounter from '@/components/QuantityCounter';
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import Modal from '@/components/Modal'
import { format } from '@/utils/format'
import videoEmbed from '@/utils/videoEmbed'
import {contaoService, datatrans} from '@/service';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
    ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		Button,
		GeneralMessage,
		Modal,
		QuantityCounter
	},
	data() {
		return {
			initialError: this.$root.options.initialError,
			content: this.$root.options.content,
			config: this.$root.options.config,
			vouchers: this.$root.options.vouchers.map(voucher => {
				const price = parseFloat(voucher.price);

				return {
					...voucher,
					custom: isNaN(price),
					price: isNaN(price) ? 0 : price,
					quantity: 0,
				};
			}),
			countryOptions: [
				{
					value: 'CH',
					label: this.$t('forms.countries.ch')
				}
			],
			form: {
				billingAddress: {
					firstname: '',
					lastname: '',
					company: '',
					street: '',
					postcode: '',
					city: '',
					countryCode: "CH",
				},
				shippingAddress: {
					firstname: '',
					lastname: '',
					company: '',
					street: '',
					postcode: '',
					city: '',
					countryCode: "CH",
				},
				phoneNumber: '',
				email: '',
				message: '',
				checkAgb: false,
				printAtHome: false,
				sendAsGift: false,
			},
			response: {
				type: 'info',
				message: '',
			},
			formLocked: false,
			videoEmbedCode: '',
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		activeVouchers() {
			return this.vouchers.filter(voucher => voucher.quantity);
		},
		showSum() {
			return this.vouchers.reduce((p, voucher) => {
				return p || !!voucher.quantity;
			}, false);
		},
		totalCount() {
			return this.vouchers.reduce((p, voucher) => {
				return p + voucher.quantity;
			}, 0);
		},
		total() {
			return this.vouchers.reduce((p, voucher) => {
				return p + (voucher.quantity * (voucher.price * 100));
			}, 0);
		}
	},
	methods: {
    joinErrorMessage(errors) {
			let messages = [];
			Object.values(errors).filter(value => !!value.join('').trim()).forEach(m => {
				const message = m.join('').trim();
				if (!messages.includes(message)) {
					messages.push(message);
				}
			});
			return messages.join(' ');
		},
		onSub(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = Math.max(voucher.quantity - 1, 0);
			}
			else {
				voucher.quantity = 0;
			}
		},
		onAdd(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = voucher.quantity + 1;
			}
			else {
				voucher.quantity = 0;
			}
		},
		onInput(voucher) {
			if (voucher.price > 0) {
				voucher.quantity = voucher.quantity || 1;
			}
			else {
				voucher.quantity = 0;
			}
		},
		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.shippingAddress, 'postcode', value.replace(/[^\d]/ig,'').substr(0,4));
				this.$refs.zipInput.$refs.input.value = this.form.shippingAddress.postcode;
			}
		},
		onZipInputBilling(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.billingAddress, 'postcode', value.replace(/[^\d]/ig,'').substr(0,4));
				this.$refs.zipInputBilling.$refs.input.value = this.form.billingAddress.postcode;
			}
		},
		onVideoClick(url) {
			this.videoEmbedCode = videoEmbed(url);
			this.$refs.videoModal.open();
		},
		scrollToForm() {
      const header = document.querySelector('.header');
			if (this.$refs.form) {
				scrrroll.to(this.$refs.form.$el.offsetTop - header.clientHeight);
			}
		},
		async onSubmit(e) {
			e.preventDefault();

			const valid = await this.$refs.form.validate();

			if (valid) {
				this.loading = true;
				try {


					if (this.form.printAtHome) {
						Object.keys(this.form.shippingAddress).forEach(key => {
							Vue.set(this.form.shippingAddress, key, this.form.billingAddress[key]);
						});
						Vue.set(this.form, 'sendAsGift', false);
					}
					else if (!this.form.sendAsGift) {
						Object.keys(this.form.billingAddress).forEach(key => {
							Vue.set(this.form.billingAddress, key, this.form.shippingAddress[key]);
						});
					}

					const response = await contaoService.orderVouchers({
						...this.form,
						vouchers: [...this.activeVouchers],
						lang: this.language
					});

					if (response.status) {
						this.response.message = '';
						this.loading = false;

						let result = await datatrans.payment({
							amount: this.total,
							ref: response.ref,
							merchantId: this.config.merchantId,
							sign: response.sign,
							successUrl: format(this.config.successUrl, {ref: response.ref}),
							cancelUrl: format(this.config.cancelUrl, {ref: response.ref}),
							errorUrl: format(this.config.errorUrl, {ref: response.ref}),
							additionalData: {
								action: 'buy',
								language: this.language,
								uppCustomerDetails: "yes",
								uppCustomerTitle: "",
								uppCustomerFirstName: this.form.billingAddress.firstname,
								uppCustomerLastName: this.form.billingAddress.lastname,
								uppCustomerStreet: this.form.billingAddress.street,
								uppCustomerStreet2: "",
								uppCustomerCity: this.form.billingAddress.city,
								uppCustomerCountry: this.form.billingAddress.countryCode,
								uppCustomerZipCode: this.form.billingAddress.postcode,
								uppCustomerPhone: this.form.phoneNumber,
								uppCustomerEmail: this.form.email,
								useAlias: true,
								uppRememberMe: "checked"
							}
						});
					}
					else {
						this.response.message = response.error;
						this.response.type = 'error';
						this.$nextTick(() => {
							this.$refs.message.$el.scrollIntoView();
						});
						this.loading = false;
					}
				}
				catch(error) {
					this.response.message = error.message;
					this.response.type = 'error';
					this.loading = false;
				}
			}
		},
		resetForm() {
			this.$refs.form && this.$refs.form.reset();
			this.form.billingAddress.firstname = '';
			this.form.billingAddress.lastname = '';
			this.form.billingAddress.company = '';
			this.form.billingAddress.street = '';
			this.form.billingAddress.postcode = '';
			this.form.billingAddress.city = '';
			this.form.shippingAddress.firstname = '';
			this.form.shippingAddress.lastname = '';
			this.form.shippingAddress.company = '';
			this.form.shippingAddress.street = '';
			this.form.shippingAddress.postcode = '';
			this.form.shippingAddress.city = '';
			this.form.phoneNumber = '';
			this.form.email = '';
			this.form.message = '';
			this.form.checkAgb = false;
			this.form.printAtHome = false;
			this.form.sendAsGift = false;
			this.vouchers.forEach(voucher => {
				voucher.quantity = 0;

				if (voucher.custom) {
					voucher.price = 0;
				}
			});
		},
	},
	created() {
		this.loading = true;
	},
	mounted() {
		this.loading = false;
	}
}
</script>

<style>

</style>
